import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import LinkIcon from '@mui/icons-material/Link'
import { Box, Stack } from '@mui/material'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { bshgenAPI } from '~/api'
import {
  ActionButton,
  SubmitButton,
  TextField,
  Title,
  useConfirm,
} from '~/components'
import { useCanEdit } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import { copyTextToClipboard, showToast } from '~/utils'

const Item = (props) => {
  const { api, item, space } = props
  const editable = useCanEdit()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { show } = useConfirm()

  const [icons, setIcons] = useState(item.icons ?? '')
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)

  const exportUrl = `${api}/${item.url}`

  const exportHtml = useCallback(async () => {
    const endpoint = `${api.replace(/^\/api\//, '')}/${item.url}/validate`
    const validation = await bshgenAPI(endpoint)

    if (!validation?.valid) {
      const missingCaption = validation.warnings?.find(
        (warning) => warning.code === 'missing_anim_caption'
      )
      const names =
        missingCaption?.paragraphs
          ?.map((paragraph) => paragraph.name)
          .filter(Boolean) ?? []

      const confirmed = await new Promise((resolve) => {
        show({
          title: t('export.missing-anim-caption.title'),
          body: (
            <>
              <p>{t('export.missing-anim-caption.body')}</p>
              {names.length > 0 && (
                <ul>
                  {names.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              )}
            </>
          ),
          actions: [
            {
              id: 'export',
              name: t('export.missing-anim-caption.confirm'),
              variant: 'contained',
            },
            {
              id: 'cancel',
              name: t('ui.cancel'),
              variant: 'outlined',
            },
          ],
          onClose: (action) => resolve(action === 'export'),
        })
      })

      if (!confirmed) {
        return
      }
    }

    window.location.href = exportUrl
  }, [api, exportUrl, item.url, show, t])

  const style = space ? { paddingTop: '1rem' } : {}

  useEffect(() => {
    if (!copied) return
    const intId = setTimeout(() => setCopied(false), 5000)
    return () => clearTimeout(intId)
  }, [copied])

  const buttonProps = useMemo(() => {
    if (['ceneo-link', 'ceneo-spec-link'].includes(item.url)) {
      return {
        onClick: () => {
          dispatch(bshSlice.createXML({ data: { url: exportUrl } }))
        },
      }
    }

    if (item.type === 'html') {
      return {
        onClick: async () => {
          setSaving(true)
          try {
            await exportHtml()
          } finally {
            setSaving(false)
          }
        },
      }
    }

    return { href: exportUrl }
  }, [dispatch, exportHtml, exportUrl, item.type, item.url])

  return (
    <tr>
      <td
        style={{
          ...style,
          padding: `${space ? '1rem' : '0.3rem'} 1rem 0.3rem 0`,
        }}
      >
        <SubmitButton
          label={item.name}
          loading={saving}
          size="small"
          sx={{ width: '100%' }}
          variant="contained"
          {...buttonProps}
        />
      </td>
      <td style={{ ...style, width: '100px' }}>
        {editable && (
          <TextField
            editable
            size="small"
            type="number"
            value={icons}
            onChange={async ({ target: { value } }) => {
              setIcons(value)
              setSaving(true)
              await dispatch(
                bshSlice.updateExport({ data: { ...item, icons: value } })
              )
              setSaving(false)
            }}
          />
        )}
      </td>
      <td style={style}>
        {item.remote && (
          <ActionButton
            icon={<LinkIcon />}
            title={t(copied ? 'ui.copied' : 'ui.copy-link')}
            size="large"
            onClick={() => {
              const copied = copyTextToClipboard(item.remote)
              if (copied) {
                setCopied(true)
              } else {
                showToast(t('ui.copy-failed'), { variant: 'error' })
              }
            }}
          />
        )}
      </td>
    </tr>
  )
}

Item.propTypes = {
  api: PropTypes.string,
  item: PropTypes.object.isRequired,
  space: PropTypes.bool,
}

const Export = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { category, subCategory, product } = useRouteContext(Export.name)

  const exports = useSelector(bshSlice.exports)

  const owner = useMemo(
    () => product ?? subCategory ?? category,
    [category, product, subCategory]
  )

  useEffect(() => {
    if (!owner) return
    dispatch(bshSlice.getExports({ data: { owner } }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner])

  const content = useMemo(() => {
    if (!owner || !exports) return
    return Object.values(_.groupBy(exports, (item) => item.type)).map(
      (items, i) => {
        return items.map((item, j) => {
          if (item.type === 'html' && !owner?.html_url) {
            return
          }
          return (
            <Item
              key={`${i}_${j}`}
              api={item.type === 'xml' ? owner.xml_url : owner.html_url}
              item={item}
              space={i > 0 && j === 0}
            />
          )
        })
      }
    )
  }, [exports, owner])

  return owner ? (
    <Stack spacing={1.5}>
      <Title variant="h2" component="h2">
        {owner.name}
      </Title>
      <Box>
        <table style={{ width: 'auto' }}>
          <thead>
            <tr>
              <th />
              <th style={{ width: '120px' }}>{t('export.max-icons')}</th>
              <th />
            </tr>
          </thead>
          <tbody>{content}</tbody>
        </table>
      </Box>
    </Stack>
  ) : null
}

export default Export
